import { Fragment } from 'react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { formatDate, EMPTY } from '@/lib/customer-format'
import type { CustomerVehicle } from '@/lib/customer-types'
import { AdditionalDetails } from './AdditionalDetails'

/** Colour an expiry date: past → error, within 30 days → warning, else muted. */
function ExpiryDate({ iso }: { iso?: string | null }) {
  if (!iso) return <span className="text-cmd-outline">{EMPTY}</span>
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return <span className="text-cmd-outline">{EMPTY}</span>
  const days = Math.floor((d.getTime() - Date.now()) / 86_400_000)
  const tone = days < 0 ? 'text-cmd-error' : days <= 30 ? 'text-[#ffd080]' : 'text-white/80'
  return <span className={`font-mono ${tone}`}>{formatDate(iso)}</span>
}

export function CustomerVehicles({ vehicles }: { vehicles: CustomerVehicle[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Vehicles · {vehicles.length}</CardTitle>
      </CardHeader>
      <CardContent>
        {vehicles.length === 0 ? (
          <p className="text-sm text-cmd-outline">No vehicles on this fleet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-cmd-highest text-left font-label text-[0.65rem] uppercase tracking-wider text-cmd-outline">
                  <th className="py-2 pr-4">Reg</th>
                  <th className="py-2 pr-4">Type</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">MOT expiry</th>
                  <th className="py-2 pr-4">PMI due</th>
                  <th className="py-2">Tax due</th>
                </tr>
              </thead>
              <tbody>
                {vehicles.map((v, i) => {
                  const extras = v.additionalFields && Object.keys(v.additionalFields).length > 0
                  return (
                    <Fragment key={v.registration}>
                      <tr
                        className={`border-b border-cmd-highest/50 ${
                          i % 2 === 0 ? 'bg-cmd-surface' : 'bg-cmd-low'
                        } ${extras ? 'border-b-0' : ''}`}
                      >
                        <td className="py-2 pr-4 font-mono text-xs uppercase text-primary">
                          {v.registration}
                        </td>
                        <td className="py-2 pr-4 text-white/80">{v.type || EMPTY}</td>
                        <td className="py-2 pr-4 text-cmd-outline">{v.status || EMPTY}</td>
                        <td className="py-2 pr-4">
                          <ExpiryDate iso={v.motExpiry} />
                        </td>
                        <td className="py-2 pr-4">
                          <ExpiryDate iso={v.pmiDue} />
                        </td>
                        <td className="py-2">
                          <ExpiryDate iso={v.taxDueDate} />
                        </td>
                      </tr>
                      {extras && (
                        <tr
                          key={`${v.registration}-extra`}
                          className={`border-b border-cmd-highest/50 ${
                            i % 2 === 0 ? 'bg-cmd-surface' : 'bg-cmd-low'
                          }`}
                        >
                          <td colSpan={6} className="px-0 pb-3">
                            <AdditionalDetails fields={v.additionalFields} variant="inline" />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
